import React, { useCallback, useEffect, useRef, useState } from "react";
import { getVenueReviewsApi } from "../../../api/api";
import { RatingStars } from "./RatingStars";
import { formatRating, reviewMonthLabel, reviewTagLabel, reviewsCountLabel } from "./reviewsModel";
import "./reviews.css";

const PAGE_SIZE = 10;

/**
 * "Reseñas" block of the bar, club and salon detail pages: summary, cards and "Ver más".
 * `isDesktop` picks the section markup (`bd-section-stacked` vs `rmob-section`); `id` is the anchor
 * the page tabs scroll to (each page has its own convention).
 */
const ReviewsSection = ({ barId, isDesktop = true, id = "sec-resenas" }) => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const owner = useRef(0);

  const load = useCallback(async (nextPage) => {
    if (!barId) return;
    const mine = ++owner.current;
    setLoading(true);
    setError("");
    try {
      const response = await getVenueReviewsApi(barId, { page: nextPage, limit: PAGE_SIZE });
      if (mine !== owner.current) return;
      const info = response?.data?.info || {};
      setSummary(info.summary || null);
      setItems((current) => (nextPage === 1 ? info.items || [] : [...current, ...(info.items || [])]));
      setPage(nextPage);
      setHasMore(Boolean(info.hasMore));
    } catch (err) {
      if (mine === owner.current) setError(err?.response?.data?.message || "No pudimos cargar las reseñas.");
    } finally {
      if (mine === owner.current) setLoading(false);
    }
  }, [barId]);

  useEffect(() => {
    setItems([]);
    setSummary(null);
    setPage(0);
    load(1);
    return () => { owner.current += 1; };
  }, [load]);

  const count = Number(summary?.ratingCount) || 0;
  const hasReviews = count > 0 && items.length > 0;

  return (
    <div id={id} className={isDesktop ? "bd-section-stacked" : "rmob-section"} data-testid="reviews-section">
      <h3 className={isDesktop ? "section-title" : "rmob-section-title"}>Reseñas</h3>

      {hasReviews && (
        <div className="rv-summary">
          <span className="rv-summary-avg">{formatRating(summary.ratingAvg)}</span>
          <RatingStars value={summary.ratingAvg} size={20} />
          <span className="rv-summary-count">{reviewsCountLabel(count)} verificadas</span>
        </div>
      )}
      <p className="rv-note">Solo pueden opinar quienes reservaron con Zoco.</p>

      {error ? (
        <div className="rv-error" role="alert">
          <span>{error}</span>
          <button type="button" className="r-btn-soft" onClick={() => load(page + 1)}>Reintentar</button>
        </div>
      ) : loading && items.length === 0 ? (
        <p className="rv-empty" role="status">Cargando reseñas…</p>
      ) : !hasReviews ? (
        <p className="rv-empty">Todavía no hay reseñas</p>
      ) : (
        <>
          <div className="rv-list">
            {items.map((review, index) => {
              const tag = reviewTagLabel(review.tag);
              return (
                <article className="rv-card" key={`${review.createdAt}-${index}`}>
                  <div className="rv-card-head">
                    <span>
                      <span className="rv-author">{review.authorName}</span>{" "}
                      <span className="rv-month">· {reviewMonthLabel(review.createdAt)}</span>
                    </span>
                    <RatingStars value={review.rating} />
                  </div>
                  {tag && <span className="rv-tag">{tag}</span>}
                  {review.comment && <p className="rv-text">{review.comment}</p>}
                </article>
              );
            })}
          </div>
          {hasMore && (
            <button type="button" className="r-btn-soft rv-more" disabled={loading} onClick={() => load(page + 1)}>
              {loading ? "Cargando…" : "Ver más"}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsSection;
